import React, { useEffect, useRef } from "react";
import { Clock, X } from "lucide-react";

/**
 * Reusable slide-in notification panel.
 *
 * Props:
 *   notifications – array of { id, text, subtext?, time, unread?, onClick? }
 *   unreadCount   – number of unread notifications (controls "Mark all read")
 *   onMarkAllRead – callback to mark every notification read
 *   onClose       – callback to close the panel
 */
const NotificationPanel = ({ notifications = [], unreadCount = 0, onMarkAllRead, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop — closes on outside click */}
      <div className="fixed inset-0 z-40" />

      {/* Panel */}
      <div
        ref={ref}
        className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-brand-text">Notifications</h2>
            {unreadCount > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-danger text-white text-[10px] font-bold leading-none">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && onMarkAllRead && (
              <button
                onClick={onMarkAllRead}
                className="text-xs font-semibold text-primary hover:underline px-2 py-1"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full text-brand-muted hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex flex-col gap-3 p-4 overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center py-10">
              <p className="text-sm font-medium text-brand-muted">No notifications yet</p>
              <p className="text-xs text-brand-light">You're all caught up!</p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={n.onClick}
                className={`rounded-xl px-4 py-3.5 flex gap-2.5 transition-colors
                  ${n.unread ? "bg-blue-50/70" : "bg-surface/60"}
                  ${n.onClick ? "cursor-pointer hover:bg-blue-100/60" : ""}`}
              >
                {/* Unread dot */}
                <span
                  className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${n.unread ? "bg-primary" : "bg-transparent"}`}
                  aria-hidden
                />
                <div className="flex flex-col gap-1 min-w-0">
                  <p className={`text-sm ${n.unread ? "font-semibold text-brand-text" : "font-medium text-brand-muted"}`}>
                    {n.text}
                  </p>
                  {n.subtext && (
                    <p className="text-xs text-brand-muted leading-snug">{n.subtext}</p>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-brand-light">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    {n.time}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;
